import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { BACKEND_URL } from '@/lib/backendUrl';
import { 
  DollarSign, Calendar, FileText, AlertCircle, CheckCircle, 
  Clock, ExternalLink, Download, CreditCard 
} from 'lucide-react';
import { toast } from 'sonner';


export default function HotelBillingPage({ hotelId }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    loadBillingData();
  }, [hotelId]);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const [summaryRes, invoicesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/billing/hotels/${hotelId}/summary`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/billing/hotels/${hotelId}/invoices`).then(r => r.json())
      ]);
      
      setSummary(summaryRes);
      setInvoices(invoicesRes.invoices || []);
    } catch (error) {
      console.error('Failed to load billing:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const viewInvoiceDetails = async (invoiceId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/hotels/${hotelId}/invoices/${invoiceId}`);
      const invoice = await response.json();
      setSelectedInvoice(invoice);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const submitPaymentProof = async (invoiceId) => {
    const txHash = prompt('Enter payment transaction hash:');
    if (!txHash) return;

    try {
      await fetch(`${BACKEND_URL}/api/billing/hotels/${hotelId}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_id: invoiceId,
          payment_tx_hash: txHash,
          payment_method: 'crypto',
          payment_notes: 'Payment submitted via dashboard'
        })
      });
      
      toast.success('Payment submitted for admin review');
      loadBillingData();
    } catch (error) {
      toast.error('Failed to submit payment proof');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'default';
      case 'pending': return 'secondary';
      case 'overdue': return 'destructive';
      case 'pending_review': return 'outline';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* Suspension Warning */}
      {summary?.is_suspended && (
        <Alert variant="destructive" data-testid="billing-suspension-alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Account Suspended:</strong> {summary.suspension_reason}
            <br />
            <span className="text-sm">New bookings are disabled until outstanding fees are paid.</span>
          </AlertDescription>
        </Alert>
      )}

      {/* Billing Summary */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-6 border-2" data-testid="billing-outstanding-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Outstanding Balance</span>
            <DollarSign className="w-4 h-4 text-orange-500" />
          </div>
          <p className="text-3xl font-bold mono">
            ${summary?.outstanding_balance?.toLocaleString() || '0'}
          </p>
          {summary?.overdue_count > 0 && (
            <p className="text-xs text-red-500 mt-1">
              {summary.overdue_count} overdue invoice{summary.overdue_count > 1 ? 's' : ''}
            </p>
          )}
        </Card>

        <Card className="p-6" data-testid="billing-invoices-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Total Invoices</span>
            <FileText className="w-4 h-4 text-primary" />
          </div>
          <p className="text-3xl font-bold mono">{summary?.total_invoices || 0}</p>
        </Card>

        <Card className="p-6" data-testid="billing-status-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Account Status</span>
            {summary?.is_suspended ? (
              <AlertCircle className="w-4 h-4 text-red-500" />
            ) : (
              <CheckCircle className="w-4 h-4 text-green-500" />
            )}
          </div>
          <Badge variant={summary?.is_suspended ? 'destructive' : 'default'}>
            {summary?.is_suspended ? 'Suspended' : 'Active'}
          </Badge>
        </Card>
      </div>

      {/* How It Works */}
      <Card className="p-6 bg-primary/5" data-testid="billing-info-card">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" /> How Billing Works
        </h3>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>• <strong>Crypto payments (Base/Polygon/Ethereum):</strong> Fees collected automatically via smart contract</p>
          <p>• <strong>Bitcoin, Solana, Pay-at-Property:</strong> Monthly invoices sent at month-end</p>
          <p>• <strong>Payment due:</strong> Within 7 days of invoice date</p>
          <p>• <strong>Late payment:</strong> Account suspended, new bookings disabled until paid</p>
        </div>
      </Card>

      {/* Invoices List */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-primary" /> Invoices
        </h3>

        {invoices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>No invoices yet</p>
            <p className="text-xs mt-1">Invoices are generated at the end of each month</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <div 
                key={invoice.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => viewInvoiceDetails(invoice.id)}
                data-testid={`invoice-${invoice.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm font-medium">{invoice.invoice_number}</span>
                    <Badge variant={getStatusColor(invoice.status)}>
                      {invoice.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Billing Period: {invoice.billing_period} | {invoice.total_bookings} booking{invoice.total_bookings !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Due: {new Date(invoice.due_date).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="text-right">
                  <p className="text-lg font-bold mono">${invoice.platform_fees_owed?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">
                    from ${invoice.gross_revenue?.toLocaleString()} gross
                  </p>
                </div>

                {invoice.status === 'pending' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      submitPaymentProof(invoice.id);
                    }}
                    data-testid={`pay-invoice-${invoice.id}`}
                  >
                    <CreditCard className="w-3 h-3 mr-1" /> Submit Payment
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Invoice Details Modal/Drawer */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedInvoice(null)}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold heading-font">Invoice Details</h2>
              <Button variant="ghost" size="sm" onClick={() => setSelectedInvoice(null)}>✕</Button>
            </div>

            <div className="space-y-4">
              {/* Invoice Header */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Invoice Number</p>
                  <p className="font-mono font-medium">{selectedInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge variant={getStatusColor(selectedInvoice.status)}>{selectedInvoice.status}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Invoice Date</p>
                  <p className="text-sm">{new Date(selectedInvoice.invoice_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Due Date</p>
                  <p className="text-sm">{new Date(selectedInvoice.due_date).toLocaleDateString()}</p>
                </div>
              </div>

              <Separator />

              {/* Summary */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Gross Revenue ({selectedInvoice.total_bookings} bookings)</span>
                  <span className="font-mono">${selectedInvoice.gross_revenue?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Your Revenue</span>
                  <span className="font-mono">${selectedInvoice.hotel_net_revenue?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-primary">
                  <span>Platform Fees Owed</span>
                  <span className="font-mono">${selectedInvoice.platform_fees_owed?.toLocaleString()}</span>
                </div>
              </div>

              <Separator />

              {/* Line Items */}
              <div>
                <h4 className="font-semibold mb-3 text-sm">Booking Breakdown</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedInvoice.line_items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs p-2 bg-accent/30 rounded">
                      <div>
                        <p className="font-medium">{item.guest_name}</p>
                        <p className="text-muted-foreground">{item.check_in} → {item.check_out}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono">${item.booking_amount}</p>
                        <p className="text-muted-foreground">Fee: ${item.platform_fee} ({item.fee_percentage}%)</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedInvoice.status === 'pending' && (
                <>
                  <Separator />
                  <Button 
                    className="w-full" 
                    onClick={() => {
                      submitPaymentProof(selectedInvoice.id);
                      setSelectedInvoice(null);
                    }}
                  >
                    <CreditCard className="w-4 h-4 mr-2" /> Submit Payment Proof
                  </Button>
                </>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
